import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ER_COPY } from "@/lib/expert-requests-copy";
import { citiesLabel, ER_LIMITS, isRequestOpen } from "@/lib/expert-requests-logic";
import { normalizeHref, socialLabel, telegramHref } from "@/lib/social-links";
import { fetchPublishedEventCounts, type ExpertRequestRow } from "@/lib/expert-requests-data";

export const Route = createFileRoute("/_authenticated/expert-requests_/$requestId")({
  head: () => ({
    meta: [
      { title: "Запрос эксперта — TurkeyDrive" },
      { name: "description", content: "Эксперт ищет площадку для своего мероприятия в Турции." },
      { property: "og:title", content: "Запрос эксперта — TurkeyDrive" },
      {
        property: "og:description",
        content: "Эксперт ищет площадку для своего мероприятия в Турции.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RequestPage,
});

type Person = {
  user_id: string;
  full_name: string;
  city: string | null;
  photo_url: string | null;
  telegram: string | null;
  social_links: string[] | null;
  expert_category?: string | null;
};
type Offer = {
  id: string;
  organizer_user_id: string;
  message: string;
  proposed_date: string | null;
  proposed_place: string | null;
  status: string;
  created_at: string;
};

function Contacts({ p }: { p: Person }) {
  const tg = telegramHref(p.telegram);
  const socials = (p.social_links ?? []).filter((s) => s?.trim());
  if (!tg && !socials.length) return <p className="text-sm text-td-muted">{ER_COPY.noContact}</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {tg && (
        <a
          href={tg}
          target="_blank"
          rel="noopener noreferrer"
          className="cta-solid inline-flex min-h-9 items-center rounded-full px-4 text-sm font-semibold"
        >
          Telegram
        </a>
      )}
      {socials
        .filter((u) => normalizeHref(u) !== tg)
        .map((u) => (
          <a
            key={u}
            href={normalizeHref(u)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-9 items-center rounded-full border border-td-line px-4 text-sm text-td-dark"
          >
            {socialLabel(u)}
          </a>
        ))}
    </div>
  );
}

function Avatar({ p }: { p: Person }) {
  return p.photo_url ? (
    <img
      loading="lazy"
      decoding="async"
      src={p.photo_url}
      alt={p.full_name}
      className="size-10 shrink-0 rounded-full object-cover"
    />
  ) : (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-td-soft font-semibold text-td-dark">
      {(p.full_name || "?").charAt(0)}
    </div>
  );
}

function RequestPage() {
  const { requestId } = Route.useParams();
  const { user } = useAuth();
  const uid = user?.id;
  const qc = useQueryClient();
  const key = ["expert-requests", "detail", requestId, uid];

  const { data, isLoading } = useQuery({
    queryKey: key,
    enabled: !!uid,
    queryFn: async () => {
      const { data: r } = await supabase
        .from("expert_event_requests")
        .select("*")
        .eq("id", requestId)
        .maybeSingle();
      if (!r) return null;
      const req = r as ExpertRequestRow;
      const [{ data: offers }, { data: me }] = await Promise.all([
        supabase
          .from("expert_event_offers")
          .select("*")
          .eq("request_id", requestId)
          .order("created_at", { ascending: true }),
        supabase.from("profiles").select("hosts_for_experts").eq("user_id", uid!).maybeSingle(),
      ]);
      const offs = (offers ?? []) as Offer[];
      const ids = [req.expert_user_id, ...offs.map((o) => o.organizer_user_id)];
      const [{ data: people }, counts] = await Promise.all([
        supabase
          .from("profiles")
          .select("user_id, full_name, city, photo_url, telegram, social_links, expert_category")
          .in("user_id", ids),
        fetchPublishedEventCounts(offs.map((o) => o.organizer_user_id)),
      ]);
      const byId = Object.fromEntries(((people ?? []) as Person[]).map((p) => [p.user_id, p]));
      return { req, offers: offs, byId, counts, hosts: !!me?.hosts_for_experts };
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["expert-requests"] });

  if (isLoading || !uid)
    return (
      <main className="mx-auto max-w-[720px] px-4 py-10 text-sm text-td-muted">Загружаем…</main>
    );
  if (!data) {
    return (
      <main className="mx-auto max-w-[720px] px-4 py-10">
        <Link to="/expert-requests" className="text-sm text-td-muted">
          {ER_COPY.back}
        </Link>
        <p className="mt-4 text-td-dark">{ER_COPY.notFound}</p>
      </main>
    );
  }
  const { req, offers, byId, counts, hosts } = data;
  const isOwner = req.expert_user_id === uid;
  const expert = byId[req.expert_user_id];
  const myOffer = offers.find((o) => o.organizer_user_id === uid);
  const open = isRequestOpen(req);

  const setStatus = async (id: string, status: "accepted" | "declined") => {
    const { error } = await supabase.from("expert_event_offers").update({ status }).eq("id", id);
    if (error) return void toast.error(error.message);
    await refresh();
  };

  return (
    <div className="min-h-screen min-w-0 bg-white">
      <main className="mx-auto min-w-0 max-w-[720px] px-4 py-6 md:py-10">
        <Link
          to="/expert-requests"
          search={{ tab: isOwner ? "mine" : "requests" }}
          className="text-sm text-td-muted hover:text-td-dark"
        >
          {ER_COPY.back}
        </Link>
        {isOwner && (
          <p className="mt-4 text-[13px] font-semibold uppercase tracking-wide text-td-teal">
            {ER_COPY.myRequestTitle}
          </p>
        )}
        <h1
          className={`${isOwner ? "mt-1" : "mt-4"} break-words font-serif text-2xl text-td-dark md:text-3xl`}
        >
          {req.topic}
        </h1>
        {req.status !== "open" && (
          <p className="mt-1 text-sm text-td-muted">{ER_COPY.statusClosed}</p>
        )}
        {req.description && (
          <p className="mt-3 whitespace-pre-wrap break-words text-sm text-td-dark">
            {req.description}
          </p>
        )}
        <dl className="mt-4 grid gap-2 rounded-2xl bg-td-soft/40 p-4 text-sm">
          <div>
            <dt className="inline text-td-muted">{ER_COPY.cities}: </dt>
            <dd className="inline text-td-dark">{citiesLabel(req.cities)}</dd>
          </div>
          {req.period && (
            <div>
              <dt className="inline text-td-muted">{ER_COPY.when}: </dt>
              <dd className="inline text-td-dark">{req.period}</dd>
            </div>
          )}
          {req.audience_size && (
            <div>
              <dt className="inline text-td-muted">{ER_COPY.audience}: </dt>
              <dd className="inline text-td-dark">{req.audience_size}</dd>
            </div>
          )}
          {req.conditions && (
            <div>
              <dt className="inline text-td-muted">{ER_COPY.conditions}: </dt>
              <dd className="inline break-words text-td-dark">{req.conditions}</dd>
            </div>
          )}
        </dl>

        {!isOwner && expert && (
          <div className="mt-4 flex min-w-0 items-center gap-3 rounded-2xl border border-td-line p-3">
            <Avatar p={expert} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-td-dark">{expert.full_name}</p>
              <p className="truncate text-xs text-td-muted">
                {[ER_COPY.expertLabel, expert.expert_category, expert.city]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            <Link
              to="/people/$userId"
              params={{ userId: expert.user_id }}
              className="shrink-0 text-sm font-semibold text-td-teal underline underline-offset-2"
            >
              {ER_COPY.openProfile}
            </Link>
          </div>
        )}

        {isOwner ? (
          <section className="mt-6">
            <h2 className="text-lg font-semibold text-td-dark">{ER_COPY.offersTitle}</h2>
            {!offers.length ? (
              <p className="mt-2 rounded-2xl bg-td-soft/60 p-4 text-sm text-td-dark">
                {ER_COPY.offersEmpty}
              </p>
            ) : (
              <ul className="mt-3 space-y-3">
                {offers.map((o) => {
                  const org = byId[o.organizer_user_id];
                  if (!org) return null;
                  return (
                    <li key={o.id} className="rounded-2xl border border-td-line p-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <Avatar p={org} />
                        <div className="min-w-0 flex-1">
                          <Link
                            to="/people/$userId"
                            params={{ userId: org.user_id }}
                            className="block truncate font-semibold text-td-dark hover:underline"
                          >
                            {org.full_name}
                          </Link>
                          <p className="truncate text-xs text-td-muted">
                            {[org.city, ER_COPY.eventsCount(counts[org.user_id] ?? 0)]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </div>
                      </div>
                      <p className="mt-3 whitespace-pre-wrap break-words text-sm text-td-dark">
                        {o.message}
                      </p>
                      {(o.proposed_date || o.proposed_place) && (
                        <p className="mt-1 text-sm text-td-muted">
                          {[o.proposed_date, o.proposed_place].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      {o.status === "sent" ? (
                        <div className="mt-3 flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => void setStatus(o.id, "accepted")}
                          >
                            {ER_COPY.accept}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => void setStatus(o.id, "declined")}
                          >
                            {ER_COPY.decline}
                          </Button>
                        </div>
                      ) : o.status === "accepted" ? (
                        <div className="mt-3 space-y-2">
                          <p className="text-sm font-semibold text-td-teal">
                            {ER_COPY.accepted} · {ER_COPY.contactTitle}
                          </p>
                          <Contacts p={org} />
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-td-muted">{ER_COPY.declined}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ) : myOffer ? (
          <section className="mt-6 rounded-2xl border border-td-line p-4">
            {myOffer.status === "sent" ? (
              <>
                <p className="font-semibold text-td-dark">{ER_COPY.offerSent}</p>
                <p className="mt-2 whitespace-pre-wrap break-words text-sm text-td-dark">
                  {myOffer.message}
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={async () => {
                    const { error } = await supabase
                      .from("expert_event_offers")
                      .delete()
                      .eq("id", myOffer.id);
                    if (error) return void toast.error(error.message);
                    toast.success(ER_COPY.withdrawn);
                    await refresh();
                  }}
                >
                  {ER_COPY.withdraw}
                </Button>
              </>
            ) : myOffer.status === "accepted" && expert ? (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-td-teal">{ER_COPY.acceptedForOrganizer}</p>
                <Contacts p={expert} />
              </div>
            ) : (
              <p className="text-sm text-td-muted">{ER_COPY.declinedForOrganizer}</p>
            )}
          </section>
        ) : hosts && open ? (
          <OfferForm requestId={req.id} uid={uid} onSent={refresh} />
        ) : null}
      </main>
    </div>
  );
}

function OfferForm({
  requestId,
  uid,
  onSent,
}: {
  requestId: string;
  uid: string;
  onSent: () => Promise<void>;
}) {
  const [message, setMessage] = useState("");
  const [date, setDate] = useState("");
  const [place, setPlace] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!message.trim()) return void toast.error(ER_COPY.offerRequired);
    setSaving(true);
    const { error } = await supabase.from("expert_event_offers").insert({
      request_id: requestId,
      organizer_user_id: uid,
      message: message.trim(),
      proposed_date: date.trim() || null,
      proposed_place: place.trim() || null,
    });
    setSaving(false);
    if (error) return void toast.error(error.message);
    toast.success(ER_COPY.offerSent);
    await onSent();
  };
  return (
    <section className="mt-6 rounded-2xl border border-td-line p-4">
      <h2 className="text-lg font-semibold text-td-dark">{ER_COPY.offerFormTitle}</h2>
      <label className="mt-3 block text-sm font-medium text-td-dark" htmlFor="of-msg">
        {ER_COPY.oMessage}
      </label>
      <Textarea
        id="of-msg"
        className="mt-2"
        rows={4}
        maxLength={ER_LIMITS.message}
        placeholder={ER_COPY.oMessagePh}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      <label className="mt-3 block text-sm font-medium text-td-dark" htmlFor="of-date">
        {ER_COPY.oDate} <span className="font-normal text-td-muted">({ER_COPY.optional})</span>
      </label>
      <Input
        id="of-date"
        className="mt-2"
        maxLength={ER_LIMITS.date}
        value={date}
        onChange={(e) => setDate(e.target.value)}
      />
      <label className="mt-3 block text-sm font-medium text-td-dark" htmlFor="of-place">
        {ER_COPY.oPlace} <span className="font-normal text-td-muted">({ER_COPY.optional})</span>
      </label>
      <Input
        id="of-place"
        className="mt-2"
        maxLength={ER_LIMITS.place}
        value={place}
        onChange={(e) => setPlace(e.target.value)}
      />
      <Button
        type="button"
        className="mt-4 h-11 w-full"
        disabled={saving}
        onClick={() => void submit()}
      >
        {ER_COPY.sendOffer}
      </Button>
    </section>
  );
}
